import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import AddIcon from "@mui/icons-material/Add";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Avatar, Box, Button, Card, CardContent, CardHeader, Divider, Grid, IconButton, Stack, Typography, } from "@mui/material";
import React, { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDeleteLeague, useRetrieveLeague, useRetrieveLeagueTeams, } from "../api/LeagueApiProvider";
import { useListLeagueHomeVenues } from "../api/LeagueVenueApiProvider";
import { useRetrieveUserLeaguePermissions } from "../api/PermissionApiProvider";
import { useRetrieveSeasonsByLeagueId } from "../api/SeasonApiProvider";
import { useRetrievePlayerByUserId } from "../ApiProvider";
import { UserContext } from "../auth/UserProvider";
import { FeedCard } from "../feed/FeedCard";
import { ErrorView } from "../nav/Error";
import { Forbidden } from "../nav/Forbidden";
import { Loading } from "../nav/Loading";
import { NotFound } from "../nav/NotFound";
import { PlayerView } from "../players/PlayerView";
import { SeasonStatusChip } from "../seasons/SeasonStatusChip";
import { DeleteDialog } from "../utils/DeleteDialog";
import ErrorSnackbar from "../utils/ErrorSnackbar";
import { useNumericParams } from "../utils/useNumericParams";
import { VenuesCard } from "../venues/VenuesCard";
import { LeagueTeamsCard } from "./LeagueTeamsCard";
const MAX_DESC = 240;
export const League = () => {
    const { id } = useNumericParams();
    const navigate = useNavigate();
    const { user } = useContext(UserContext);
    const { data: league, isLoading, error } = useRetrieveLeague({ id });
    const { data: admin, isLoading: isAdminLoading, error: adminError, } = useRetrievePlayerByUserId({
        userId: league?.createdBy ? Number(league.createdBy) : undefined,
        enabled: !!user,
    });
    const [expanded, setExpanded] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const { data: permissions, isLoading: isPermsLoading, error: permsError, } = useRetrieveUserLeaguePermissions({
        leagueId: id,
    });
    const { canView = false, canModify = false } = permissions || {};
    const { data: seasons, isLoading: isSeasonsLoading, error: seasonsError, } = useRetrieveSeasonsByLeagueId({ leagueId: id, enabled: canView });
    const { data: teams, isLoading: isTeamsLoading, error: teamsError, } = useRetrieveLeagueTeams({ leagueId: id, enabled: canView });
    const { data: leagueVenues, isLoading: isVenuesLoading, error: venuesError, } = useListLeagueHomeVenues({ leagueId: id, enabled: canView });
    const deleteMutation = useDeleteLeague({
        id,
        onMutate: () => setDeleteLoading(true),
        onSuccess: () => {
            navigate("/leagues");
        },
        onError: () => {
            setDeleteLoading(false);
        },
    });
    if (!id) {
        return _jsx(NotFound, {});
    }
    if (isLoading ||
        deleteLoading ||
        (isAdminLoading && !!user) ||
        isSeasonsLoading ||
        isPermsLoading ||
        isTeamsLoading ||
        isVenuesLoading) {
        return _jsx(Loading, {});
    }
    // If league failed to load, show NotFound (404)
    if (!league || error) {
        return _jsx(NotFound, {});
    }
    // If permissions failed to load, show error
    if (permsError) {
        return (_jsx(ErrorView, { error: permsError || new Error("Failed to check league permissions.") }));
    }
    if (!canView) {
        return _jsx(Forbidden, {});
    }
    if (seasonsError || teamsError || venuesError || adminError) {
        return (_jsx(ErrorView, { error: seasonsError ||
                teamsError ||
                venuesError ||
                adminError ||
                new Error("Failed to load league.") }));
    }
    const description = league.description || "";
    const isLong = description.length > MAX_DESC;
    const shownDescription = !isLong || expanded
        ? description
        : description.slice(0, MAX_DESC).trimEnd() + "…";
    return (_jsxs(React.Fragment, { children: [_jsx(ErrorSnackbar, { open: deleteMutation.isError, message: "Failed to delete league. Please try again.", onClose: () => { } }), _jsxs(Card, { sx: { mb: 3 }, children: [_jsx(CardHeader, { avatar: _jsx(Avatar, { sx: { width: 56, height: 56 }, src: league.logo || "", alt: league.name, children: !league.logo && league.name.match(/\b(\w)/g)?.join("") }), title: _jsx(Typography, { variant: "h5", children: league.name }), subheader: league.createdAt ? (_jsxs(Typography, { variant: "caption", fontStyle: "italic", children: ["Founded \u2022 ", new Date(league.createdAt).getFullYear()] })) : undefined, action: canModify && (_jsxs(Stack, { direction: "row", spacing: 1, children: [_jsx(Link, { to: `/leagues/${id}/edit/`, children: _jsx(IconButton, { "aria-label": "edit", children: _jsx(EditIcon, {}) }) }), _jsx(IconButton, { "aria-label": "delete", onClick: () => setShowDelete(true), children: _jsx(DeleteIcon, {}) })] })) }), description && description.length > 0 && (_jsx(CardContent, { children: _jsxs(Typography, { variant: "body2", color: "text.secondary", component: "div", children: [shownDescription, isLong && (_jsx(Button, { size: "small", onClick: () => setExpanded((p) => !p), sx: {
                                        ml: 1,
                                        textTransform: "none",
                                        padding: 0,
                                        minWidth: "auto",
                                    }, children: expanded ? "Show less" : "Read more" }))] }) }))] }), _jsx(DeleteDialog, { open: showDelete, title: `Delete ${league.name}?`, onCancel: () => setShowDelete(false), onConfirm: () => deleteMutation.mutate() }), _jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { item: true, xs: 12, md: 9, order: { xs: 2, md: 1 }, children: _jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { item: true, xs: true, children: _jsx(Typography, { variant: "h6", children: "Seasons" }) }), _jsx(Grid, { item: true, children: permissions?.canModify && (_jsx(Box, { display: "flex", justifyContent: "flex-end", children: _jsx(Button, { variant: "contained", size: "small", component: Link, to: `/leagues/${id}/seasons/create/`, startIcon: _jsx(AddIcon, {}), children: "Create Season" }) })) }), _jsx(Grid, { item: true, xs: 12, children: seasons && seasons.length > 0 ? (_jsx(Stack, { spacing: 2, children: seasons.map((s) => (_jsx(FeedCard, { icon: _jsx(CalendarMonthIcon, {}), iconColor: "#F57C00", title: s.name, subtext: "", timestamp: s.createdAt, to: `/leagues/${id}/seasons/${s.id}`, action: _jsx(SeasonStatusChip, { season: s, size: "small" }) }, s.id))) })) : (_jsx(Typography, { variant: "body2", color: "text.secondary", children: "No seasons yet." })) })] }) }), _jsx(Grid, { item: true, xs: 12, md: 3, order: { xs: 1, md: 2 }, children: _jsxs(Stack, { spacing: 2, children: [_jsx(VenuesCard, { venues: leagueVenues
                                        ?.filter((lv) => lv.isActive)
                                        .map((lv) => lv.venue) || [], canModify: canModify, title: "Home Venues", manageUrl: `/leagues/${id}/venues/`, emptyStateDescription: "If your league plays at a dedicated venue, add it here to keep everything in one place.", linkable: !!user }), admin && (_jsx(Card, { children: _jsxs(CardContent, { children: [_jsx(Typography, { variant: "h6", children: "Administrator" }), _jsx(Divider, { sx: { my: 2 } }), _jsx(PlayerView, { player: admin, variant: "subtitle1" })] }) })), teams && (_jsx(LeagueTeamsCard, { teams: teams, leagueId: id, canModify: canModify, linkable: !!user }))] }) })] })] }));
};
